import Base from '@netx/core/lib/netx-model';
import {i18n} from '@netx/core/i18n';

import assetCustomConfig from '@netx/core-asset/config';

import {deepObjectExtend} from '@netx/core-next/utils/object';

/**
 * UserPrefs: Stores the user preferences returned by the server
 *
 * @class
 */
// TODO: Parsing user prefs into a multi-level object delimited by dots
// was an interesting exercise, but it's quite complicated, leads to a
// non-standard interface, and doesn't actually deliver any benefits
// over doing it the easy way.
// (Hindsight: if we only had it sooner ...)
// This code is tested & works & we should't mess with it for no reason,
// but rather than ever fix or extend this approach, we should just replace
// it with a very simple, generic Netx.Model where every property is a
// seperate attribute, manipulated by get/set/save.
//

export default class UserPrefs extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/** @type {array} */
	get xhrTrackProgress() {
		return ['getUserPreferences'];
	}

	/**
	 * @type {object}
	 */
	get xhrLoadingMessages() {
		return {
			getUserPreferences: 'Loading user preferences',
		};
	}

	/**
	 * @type {object}
	 */
	get xhrLoadedMessages() {
		return {
			getUserPreferences: 'Loaded user preferences',
		};
	}

	/**
	 * All of our user prefs are in dot.syntax and then we parse with `_.lvbsToDeepObject` - to list them here would be exhaustive at the moment
	 * Once we have cleaned up al lthe namespace issues in our naming, though laboring, would be very useful for our docs to describe each property
	 * @property {object}  defaults                   - The default attribute values
	 * @property {array}   defaults.attributeDisplay  - The default attributes to display (used in asset list/card mode)
	 */
	get defaults() {
		return {
			attributeDisplay: [],
		};
	}

	/** @type {Array} */
	get clientAttrs() {
		return ['attributeDisplay'];
	}

	/**
	 * @property {object} methods                      - Model RPC methods
	 * @property {method} methods.read                 - Fetch user preferences
	 * @property {method} methods.saveUserPreferences  - Save user preferences
	 * @property {method} methods._setUserPreference   - Save a single user preference
	 */
	get methods() {
		return {
			/** @method */
			read: ['getUserPreferences'],
			// NOTE: this is an id-less hash (not really an object) and as such, save/update/create
			// is not the correct route to go.  instead see setUserPreferences() below.
			// NOTE: you can remove these harsh reminder methods as you see fit, :) -jon
			create: function () {
				console.error('no save/create on non-objects plz');
				return false;
			},
			update: function () {
				console.error('no save/update on non-objects plz');
				return false;
			},
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			saveUserPreferences: function (opts) {
				if (
					!_.isUndefined(this.setUserPreferenceValues) &&
					this.setUserPreferenceValues.length
				) {
					return ['setUserPreferences', this.setUserPreferenceValues];
				} else {
					return false; // do nothing at all.
				}
			},
			/**
			 * Set one preference on the server.
			 * @param {string} pref Preference to set - Dot seperated preference key "tagpanel.collection" for nested values
			 * @param {array|number|object|string} val  Model attribute value
			 * @param {object} opts sync/bRPC options
			 * @todo I would call that setPreference(); setUserPreference should be renamed savePreference() or similar. - albert einstein
			 * @see this.setPreferenceLocally
			 */
			_setUserPreference: function (pref, val, opts) {
				this.setPreferenceLocally(pref, val);
				// API call wants an LVB:
				return ['setUserPreference', {label: pref, value: val}];
			},
		};
	}

	/**
	 * @property {object}   parsers                      - Model RPC methods
	 * @property {function} parsers.read                 - Parse fetch of user preferences
	 * @property {function} parsers.saveUserPreferences  - Save user preferences
	 */
	get parsers() {
		return {
			/**
			 * @function
			 * @param {array} prefs User preferences (lvb)
			 * @param {object} opts Request options
			 */
			read: function (prefs, opts) {
				// Java programmers don't have abstract, typeless objects & therefore must encode such things in arrays of LabelValueBeans.
				return _.lvbsToDeepObject(prefs);
			},
			/**
			 * @function
			 * @param {null} response Null response
			 * @param {object} opts Request options
			 */
			saveUserPreferences: function (response, opts) {
				this.setUserPreferenceValues = [];
				this.trigger('saved');
			},
		};
	}

	/**
	 * @override
	 * @param {object} attr Model attributes
	 * @param {object} opts Model options
	 * @listens module:system#system:property:edit:success
	 */
	constructor(attrs, opts = {}) {
		super(attrs, opts);

		/** @type {string} */
		this.setUserPreferenceValue = opts.setUserPreferenceValue || '';
		this.setUserPreferenceValues = opts.setUserPreferenceValues || [];
		/** @type {array} */
		this._allChangedAttributes = opts._allChangedAttributes || [];

		////////////////////////////////////////
		// QUEUE
		////////////////////////////////////////
		/**
		 * Lets not hit the server as fast as we can
		 * @memberOf Netx.Models.UserPrefs
		 * @alias queue
		 * @type {Netx.Model}
		 */
		this.queue = new Base();
		this.addSubModel(this.queue);

		// Setup our deferred for queuing
		this.queueDeferred = new $.Deferred();
		// We will save and clear our queue after 500ms of "inactivity"
		this.listenTo(
			this.queue,
			'change',
			_.debounce(function () {
				// Do we have anything to save?
				if (!_.isEmpty(this.queue.attributes)) {
					var done = _.bind(function () {
						this.queueDeferred = new $.Deferred();
						this.queue.clear();
					}, this);
					// Map to an array of lvbs
					this.setUserPreferenceValues = _.objectToLvbs(this.queue.attributes);
					// Save on server
					this.saveUserPreferences().then(
						function () {
							this.queueDeferred.resolveWith(this, [
								deepObjectExtend({}, this.queue.attributes),
							]);
							done();
						},
						function () {
							this.queueDeferred.reject();
							done();
						},
					);
				}
			}, 500),
		);
		////////////////////////////////////////
		// !QUEUE
		////////////////////////////////////////

		// Hey, listen!
		// Listen for properties changes that affect us
		this.listenTo(dispatcher, 'system:property:edit:success', function (prop) {
			// TODO: in 8.10 we want to do this for any system property that's also a user preference:
			// if (! _.isUndefined(this.getPreference(prop.get('label')))) {
			//
			// but for 8.9 we're only adressing DAM-8338 and this one property:
			//
			// And...... for 8.11/8.12? -jse-
			if (prop.get('label') == 'adbuilder.enabled') {
				// Update with the new value
				this.setPreferenceLocally(prop.get('label'), prop.get('value'));
				this.trigger('sync', this, prop);
			}
		});

		// Fix some renamed (deprecated) pref keys
		this.listenTo(this, 'sync', function () {
			// Added in 8.11
			if (
				!this.getPreference('asset.list.type') &&
				this.getPreference('assetListType')
			) {
				this.setUserPreference(
					'asset.list.type',
					this.getPreference('assetListType'),
				);
				this.setUserPreference('assetListType', '');
			}
			// Added in 8.11
			if (
				!this.getPreference('asset.list.sort.field') &&
				this.getPreference('assetListSortField')
			) {
				this.setUserPreference(
					'asset.list.sort.field',
					this.getPreference('assetListSortField'),
				);
				this.setUserPreference('assetListSortField', '');
			}
			// Added in 8.11
			if (
				!this.getPreference('asset.list.sort.order') &&
				this.getPreference('assetListSortOrder')
			) {
				this.setUserPreference(
					'asset.list.sort.order',
					this.getPreference('assetListSortOrder'),
				);
				this.setUserPreference('assetListSortOrder', '');
			}
			// Added in 8.11
			if (
				!this.getPreference('asset.list.cardSize') &&
				this.getPreference('assetList.thumbSize')
			) {
				this.setUserPreference(
					'asset.list.cardSize',
					this.getPreference('assetList.thumbSize'),
				);
				this.setUserPreference('assetList.thumbSize', '');
			}
			// Added in 8.11
			if (
				!this.getPreference('asset.list.cardSize') &&
				this.getPreference('assetList.thumbSize')
			) {
				this.setUserPreference(
					'asset.list.cardSize',
					this.getPreference('assetList.thumbSize'),
				);
				this.setUserPreference('assetList.thumbSize', '');
			}
			// Added in 8.11
			// Fixes legacy attribute set selection as best as possible
			if (
				!this.getPreference('attribute.attributeSets.selected.gallery.card') &&
				this.getPreference('attributeSet.selected')
			) {
				this.setUserPreference(
					'attribute.attributeSets.selected.gallery.card',
					this.getPreference('attributeSet.selected'),
				);
			}
			if (
				!this.getPreference(
					'attribute.attributeSets.selected.gallery.curator',
				) &&
				this.getPreference('attributeSet.selected')
			) {
				this.setUserPreference(
					'attribute.attributeSets.selected.gallery.list',
					this.getPreference('attributeSet.selected'),
				);
			}
			if (
				!this.getPreference('attribute.attributeSets.selected.gallery.list') &&
				this.getPreference('attributeSet.selected')
			) {
				this.setUserPreference(
					'attribute.attributeSets.selected.gallery.list',
					this.getPreference('attributeSet.selected'),
				);
			}
			if (
				!this.getPreference('attribute.attributeSets.selected.tagPanel') &&
				this.getPreference('attributeSet.selected')
			) {
				this.setUserPreference(
					'attribute.attributeSets.selected.tagPanel',
					this.getPreference('attributeSet.selected'),
				);
			}
			if (
				!this.getPreference('attribute.attributeSets.selected.tagPanelGrid') &&
				this.getPreference('attributeSet.selected')
			) {
				this.setUserPreference(
					'attribute.attributeSets.selected.tagPanelGrid',
					this.getPreference('attributeSet.selected'),
				);
				this.setUserPreference('attributeSet.selected', '');
			}
			if (
				!this.getPreference('attribute.attributeSets.selected.detail') &&
				this.getPreference('asset.attribute.selected-sets')
			) {
				this.setUserPreference(
					'attribute.attributeSets.selected.detail',
					this.getPreference('asset.attribute.selected-sets'),
				);
			}
			if (
				!this.getPreference('attribute.attributeSets.selected.upload') &&
				this.getPreference('asset.attribute.selected-sets')
			) {
				this.setUserPreference(
					'attribute.attributeSets.selected.upload',
					this.getPreference('asset.attribute.selected-sets'),
				);
				this.setUserPreference('asset.attribute.selected-sets', '');
			}
			// Added in 8.12
			// Fixes legacy asset detail
			if (
				!this.getPreference('asset.detail.layout') &&
				this.getPreference('assetDetail.layout')
			) {
				this.setUserPreference(
					'asset.detail.layout',
					this.getPreference('assetDetail.layout'),
				);
				this.setUserPreference('assetDetail.layout', '');
			}
		});
	}

	/**
	 * User prefs is never new
	 * @override
	 * @return {boolean}
	 */
	isNew() {
		return false;
	}

	/**
	 * Use in tandem with setUserPreference/setUserPreferences
	 * This is a simple utility for setting one preference on the model
	 * It updates the model immediately so the preference is reflected.
	 * It can take up to 5 seconds after the request for the new/updated preference to be reflected by the server
	 * @method
	 * @param {string} pref Preference to set - Dot seperated preference key "tagpanel.collection" for nested values
	 * @param {array|number|object|string} val  Model attribute value
	 */
	setPreferenceLocally(pref, val) {
		var i, keys, prefObj, prefToSet, value;
		// ====================
		// Model local update
		// ====================
		// Convert dot formatted string to array of keys
		keys = pref.split('.');
		// New/modified pref object
		prefObj = {};
		// Iterations
		i = 0;
		// Convert array of keys to a nested object with the end value being the value passed to this method
		_.reduce(
			keys,
			function (memo, key) {
				i++;
				// Check that we still have more keys to get to
				memo[key] = i < keys.length ? {} : val;
				return memo[key];
			},
			prefObj,
		);
		// Look for existing top level pref
		prefToSet = this.get(keys[0]);
		// Has exisiting top level pref - extend it or replace it
		if (prefToSet) {
			value = prefObj[keys[0]];
			_.isObject(value)
				? this.set(keys[0], deepObjectExtend(prefToSet, value))
				: this.set(prefObj);
		}
		// No exisiting top level pref - create it all
		else {
			this.set(keys[0], prefObj[keys[0]]);
		}
		// ====================
		// !Model local update
		// ====================
	}

	/**
	 * This is a simple utility for setting one preference on the server.
	 * @method
	 * @param {string} pref Preference to set - Dot seperated preference key "tagpanel.collection" for nested values
	 * @param {array|number|object|string} val  Model attribute value
	 * @todo I would call that setPreference(); setUserPreference should be renamed savePreference() or similar. - albert einstein
	 * @see this.setPreferenceLocally
	 */
	setUserPreference(pref, val) {
		if (!pref) {
			return;
		}
		if (_.isObject(pref)) {
			return this.setUserPreferences(pref);
		}
		this.setPreferenceLocally(pref, val);
		this.queue.set(pref, val);
		return this.queueDeferred.promise();
	}
	/**
	 * This is a simple utility for setting multiple preference on the server.
	 * @method
	 * @param {object} prefs Preferences to set - Dot seperated preference key "tagpanel.collection" for nested values
	 * @todo I would call that setPreferences(); setUserPreference should be renamed savePreference() or similar. - albert einstein
	 * @see this.setPreferenceLocally
	 */
	setUserPreferences(prefs) {
		// Loop over preferences where 'val' is value and 'pref' is the [dot seperated] preference name
		_.each(
			prefs,
			function (val, pref) {
				// ====================
				// Model local update
				// ====================
				this.setPreferenceLocally(pref, val);
				// ====================
				// !Model local update
				// ====================
				// Set for update
				this.queue.set(pref, val);
			},
			this,
		);
		return this.queueDeferred.promise();
	}

	/**
	 * This does equivalent of .get(attr) but using .getDotPath('tree.val1.val2') notation for deep objects
	 * @method
	 * @pram {string} dotPath Dot path
	 * @todo maybe move by other bean/obj/tree madness in netx.js?  or even (gasp) add to all models???
	 */
	getDotPath(dotPath) {
		console.warn(
			'getDotPath() is deprecated, gone in 8.13 .  Use getPreference().',
		);
		return this.getPreference(dotPath);
	}

	/**
	 * @method
	 * @param {string} pref Preference to get - Dot seperated preference key "tagpanel.collection" for nested values
	 * @return {mixed} Prefernce value
	 */
	getPreference(pref) {
		if (!pref) {
			return;
		}

		// Vars
		var keys, val;

		// Convert dot formatted string to array of keys
		keys = pref.split('.');
		// See if we are lucky enough that this is a top level preference
		val = this.get(keys.shift());
		// Maybe not- look deeper
		while (!_.isUndefined(val) && keys.length > 0) {
			val = val[keys.shift()];
		}
		return val;
	}

	/**
	 * Retrieve locales and filter on the i18n.userSelectableLocales system property
	 * @method
	 * @return {string}
	 */
	getAvailableLocales() {
		const availableLocales = [
			{
				label: i18n.t('i18n.languageEnglish'),
				value: 'en-US',
			},
			{
				label: i18n.t('i18n.languageFrench'),
				value: 'fr-FR',
			},
			{
				label: i18n.t('i18n.languageGerman'),
				value: 'de-DE',
			},
			{
				label: i18n.t('i18n.languageJapanese'),
				value: 'ja-JP',
			},
		];
		// Get available locale keys from server
		// Returns a CSV string "en-US, fr-FR"
		let localeFilter = this.getPreference('i18n.userSelectableLocales');
		if (!localeFilter) return availableLocales;

		// If system property is set filter the system locales
		// Support mixed space or no space "en-US,fr-FR, en-GB"
		localeFilter = localeFilter.split(/[ ,]+/);
		return availableLocales.filter((locale) => {
			return localeFilter.includes(locale.value);
		});
	}
	/**
	 * Gets either the user preference or the systemwide default for sorting
	 * @method
	 * @return {string}
	 */
	getListPreference() {
		var prefType = this.getPreference('asset.list.type'),
			defaultType = assetCustomConfig.LIST_TYPE_DEFAULT;

		// make sure our preference is available on this device
		if (assetCustomConfig.LIST_TYPE.indexOf(prefType) === -1) {
			prefType = false;

			// no preference, make sure our default is available on this device
			if (assetCustomConfig.LIST_TYPE_DEFAULT.indexOf(defaultType) === -1) {
				defaultType = false;
			}
		}

		return prefType || defaultType || 'grid';
	}

	/**
	 * @method
	 * @param {string} pref Preference key
	 * @return {boolean}
	 */
	isFalse(pref) {
		const _pref = this.getPreference(pref);
		if (_pref == null) return false;
		return _pref === false || _pref.toString().toLowerCase() == 'false';
	}
	/**
	 * @method
	 * @param {string} pref Preference key
	 * @return {boolean}
	 */
	isTrue(pref) {
		const _pref = this.getPreference(pref);
		if (_pref == null) return false;
		return _pref === true || _pref.toString().toLowerCase() == 'true';
	}
	/**
	 * @method
	 * @param {string} attr Model attribute
	 */
	_rememberChanged(attr) {
		if (this._allChangedAttributes.indexOf(attr) < 0) {
			this._allChangedAttributes.push(attr);
		}
	}

	/** @type {string} */
	static get __name__() {
		return 'UserPrefs';
	}
}
